import { Box, Flex, Text, Tooltip, useBreakpointValue } from '@chakra-ui/react';
import { faFile, faTag, faUser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Button,
  Checkbox,
  FormFeedback,
  Modal,
  Table,
  useToast
} from '@morressier/alembic';
import { ITags } from '@publishing-engine/domain/models';
import ListLoader from 'src/components/ListLoader';
import ListView from 'src/layouts/ListView';
import { useForm, Controller, FieldError } from 'react-hook-form';
import { updateEditorTag } from 'src/api/mutations';
import { useState } from 'react';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
interface Props {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  buttonText: string;
  data: ITags[];
  allTags: ITags[];
}

const AddingTagsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  title,
  buttonText,
  data,
  allTags
}) => {
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      tags: allTags.map(tag => ({ id: tag.id, name: tag.name })) // Default checked tags as objects
    }
  });
  const toast = useToast();
  const selectedTags = watch('tags'); // Watch the selected tag IDs
  const isCompactTable = useBreakpointValue({ base: true, md: false }) ?? false;
  const modalWidth =
    useBreakpointValue({ base: '95vw', md: '90vw', lg: '1000px' }) ?? '1000px';
  const [deleteList, setDeleteList] = useState<
    Array<{ id: string; name: string }>
  >([]); // Objects with id and name
  const [newList, setNewList] = useState<Array<{ id: string; name: string }>>(
    []
  );
  const { workflowResponse } = useSubmissionData();
  const { submissionId, submissionContextId } = workflowResponse;
  const UpdateEditorTag = updateEditorTag(submissionId);
  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    tag: ITags
  ) => {
    const isChecked = e.target.checked;
    const Check = allTags.some(allTag => allTag.id === tag.id);
    const newSelectedTags = isChecked
      ? [...selectedTags, { id: tag.id, name: tag.name }]
      : selectedTags.filter(selectedTag => selectedTag.id !== tag.id);
    setValue('tags', newSelectedTags);

    // switch to handle deleteList and newList updates
    switch (true) {
      case Check && !isChecked:
        setDeleteList(prev => [...prev, { id: tag.id, name: tag.name }]);
        break;
      case Check && isChecked:
        setDeleteList(prev => prev.filter(item => item.id !== tag.id));
        break;
      case !Check && isChecked:
        setNewList(prev => [...prev, { id: tag.id, name: tag.name }]);
        break;
      case !Check && !isChecked:
        setNewList(prev => prev.filter(item => item.id !== tag.id));
        break;
      default:
        break;
    }
  };

  const onSubmit = async () => {
    const res = await UpdateEditorTag.mutateAsync({
      deleteList,
      selectedList: newList,
      submissionContextId
    });

    const toastTitle = [
      newList?.length > 0 &&
        `${newList.length} Tag${newList.length !== 1 ? 's' : ''} added`,
      deleteList?.length > 0 &&
        `${deleteList.length} Tag${deleteList.length !== 1 ? 's' : ''} deleted`
    ]
      .filter(Boolean)
      .join(', ');
    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: toastTitle
      });

      return onClose();
    }

    return toast({
      variant: 'error',
      title: res?.message
    });
  };
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width={modalWidth}
      closeButtonProps={{
        hidden: true
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <ListView>
          <ListView.Title>
            <Flex
              flexDirection="column"
              alignItems="start"
              marginTop={4}
              gap={2}
            >
              <Text textStyle="headingSection">{title}</Text>
              <Text textStyle="textContext">
                Select any relevant tags to classify this submission.
              </Text>
            </Flex>
          </ListView.Title>
          <ListView.Body>
            {data?.length ? (
              <Box width="100%" overflowX={isCompactTable ? 'auto' : 'visible'}>
                <Box minW={isCompactTable ? '760px' : '100%'}>
                  <Table>
                    <Table.HeaderRow>
                      <Table.HeaderCell flex="1" />
                      <Table.HeaderCell flex="8">Name</Table.HeaderCell>
                      <Table.HeaderCell flex="8">Description</Table.HeaderCell>
                      <Table.HeaderCell flex="4">Category</Table.HeaderCell>
                      {!isCompactTable && (
                        <Table.HeaderCell flex="8">
                          Category Description
                        </Table.HeaderCell>
                      )}
                      {!isCompactTable && (
                        <Table.HeaderCell flex="4">
                          Category Color
                        </Table.HeaderCell>
                      )}
                    </Table.HeaderRow>
                    <Table.Body marginBottom={3}>
                      {data?.length ? (
                        data.map(tag => (
                          <Table.Row key={tag.id} gap={5}>
                            <Table.RowCell flex="1" textStyle="headingLine">
                              <Controller
                                name="tags"
                                control={control}
                                rules={{
                                  validate: () => {
                                    return newList.length >= 1 ||
                                      deleteList.length >= 1
                                      ? true
                                      : 'The list is already up to date.';
                                  }
                                }}
                                render={() => (
                                  <Checkbox
                                    id={tag.name}
                                    name={tag.name}
                                    ariaLabel={`Select ${tag.name}`}
                                    isChecked={selectedTags.some(
                                      selectedTag => selectedTag.id === tag.id
                                    )}
                                    onChange={e => handleCheckboxChange(e, tag)}
                                  >
                                    {''}
                                  </Checkbox>
                                )}
                              />
                            </Table.RowCell>
                            <Table.RowCell flex="8" textStyle="headingLine">
                              <Badge size="context" variant="neutral">
                                <Flex
                                  gap={1}
                                  alignItems="center"
                                  color={'neutralSecondaryContent'}
                                >
                                  <FontAwesomeIcon
                                    icon={
                                      tag.usage === 'users' ? faUser : faFile
                                    }
                                  />
                                  <Tooltip
                                    hasArrow
                                    borderRadius={4}
                                    placement="top"
                                    label={tag.name}
                                  >
                                    <Text
                                      textStyle="textContext"
                                      noOfLines={1}
                                      isTruncated
                                      whiteSpace={'normal'}
                                      wordBreak={'break-word'}
                                    >
                                      {tag.name}
                                    </Text>
                                  </Tooltip>
                                </Flex>
                              </Badge>
                            </Table.RowCell>
                            <Table.RowCell
                              flex="8"
                              textStyle="headingLine"
                              isTruncated
                            >
                              <Tooltip
                                hasArrow
                                borderRadius={4}
                                placement="top"
                                label={tag.description}
                              >
                                <Box width="100%" overflow="hidden">
                                  <Text textStyle="textBody" isTruncated>
                                    {tag.description}
                                  </Text>
                                </Box>
                              </Tooltip>
                            </Table.RowCell>
                            <Table.RowCell flex="4" isTruncated>
                              <Tooltip
                                hasArrow
                                borderRadius={4}
                                placement="top"
                                label={tag.tagCategory}
                              >
                                <Text noOfLines={1} isTruncated>
                                  {tag.tagCategory}
                                </Text>
                              </Tooltip>
                            </Table.RowCell>

                            {!isCompactTable && (
                              <Table.RowCell
                                flex="8"
                                textStyle="headingLine"
                                isTruncated
                              >
                                <Tooltip
                                  hasArrow
                                  borderRadius={8}
                                  placement="top"
                                  label={tag.tagDescription}
                                >
                                  <Box width="100%" overflow="hidden">
                                    <Text textStyle="textBody" isTruncated>
                                      {tag.tagDescription}
                                    </Text>
                                  </Box>
                                </Tooltip>
                              </Table.RowCell>
                            )}
                            {!isCompactTable && (
                              <Table.RowCell
                                flex="4"
                                textStyle="headingLine"
                                isTruncated
                              >
                                <Tooltip
                                  hasArrow
                                  borderRadius={4}
                                  placement="top"
                                  label={tag.tagColor}
                                >
                                  <Box
                                    w="30px"
                                    h="30px"
                                    bg={tag.tagColor}
                                    border="1px solid"
                                    borderColor={'gray.300'}
                                    borderRadius="4px"
                                    shadow={'sm'}
                                  />
                                </Tooltip>
                              </Table.RowCell>
                            )}
                          </Table.Row>
                        ))
                      ) : (
                        <ListLoader />
                      )}
                    </Table.Body>
                  </Table>
                </Box>
              </Box>
            ) : (
              <>
                <Flex
                  justifyContent="center"
                  alignItems="center"
                  backgroundColor="neutralSecondaryFill"
                  borderRadius="small"
                  height="112px"
                >
                  <FontAwesomeIcon icon={faTag} fontSize="42px" />
                </Flex>
                <Flex flexDirection="column" alignItems="center" marginTop={4}>
                  <Text textStyle="headingGroup">No Results Found</Text>
                  <Text>
                    Please create the required tags or contact the Org Admin to
                    create the relevant tags for this journal.
                  </Text>
                </Flex>
              </>
            )}
            {(errors.tags as FieldError)?.message && (
              <FormFeedback variant="error">
                {(errors.tags as FieldError).message}
              </FormFeedback>
            )}
          </ListView.Body>
          <Flex
            justifyContent={{ base: 'stretch', md: 'flex-end' }}
            gap={2}
            alignItems={{ base: 'stretch', md: 'center' }}
            flexDirection={{ base: 'column', sm: 'row' }}
            marginTop={4}
            width="100%"
          >
            <Button
              onClick={onClose}
              variant="secondary"
              width={{ base: '100%', sm: 'auto' }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              width={{ base: '100%', sm: 'auto' }}
              isDisabled={
                !(data && data.length) ||
                (!deleteList.length && !newList.length)
              }
              disabled={
                !(data && data.length) ||
                (!deleteList.length && !newList.length)
              }
            >
              {buttonText}
            </Button>
          </Flex>
        </ListView>
      </form>
    </Modal>
  );
};

export default AddingTagsModal;
